(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabRenderer = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var ZOOM_DEFAULT = 1;
  var ZOOM_MIN = 0.4;
  var ZOOM_MAX = 4;
  var ZOOM_STEP = 0.25;

  function clampPercent(value) {
    var percent = Number(value);
    if (!Number.isFinite(percent)) return 0;
    return Math.min(100, Math.max(0, percent));
  }

  function clampZoom(value) {
    var scale = Number(value);
    if (!Number.isFinite(scale)) return ZOOM_DEFAULT;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
  }

  // `=` is the unshifted plus key on a US keyboard; numpad reports `+` / `-`
  // or the older `Add` / `Subtract` keys depending on the browser.
  function zoomDeltaFromKey(event) {
    if (!event || event.ctrlKey || event.metaKey || event.altKey) return 0;

    var key = event.key;
    if (key === '+' || key === '=' || key === 'Add') return ZOOM_STEP;
    if (key === '-' || key === '_' || key === 'Subtract') return -ZOOM_STEP;

    var code = event.code;
    if (code === 'NumpadAdd') return ZOOM_STEP;
    if (code === 'NumpadSubtract') return -ZOOM_STEP;
    return 0;
  }

  function isEditableTarget(target) {
    if (!target) return false;
    var tag = String(target.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return Boolean(target.isContentEditable);
  }

  function normalizeMediaId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }

  function imageSource(image) {
    if (!image) return '';
    return (
      image.getAttribute('data-full-src') ||
      image.currentSrc ||
      image.getAttribute('src') ||
      image.src ||
      ''
    );
  }

  function resolveProductImage(doc, mediaId) {
    var targetId = normalizeMediaId(mediaId);
    if (!targetId || !doc || typeof doc.querySelectorAll !== 'function') return '';

    var media = doc.querySelectorAll('[data-media-id]');
    for (var index = 0; index < media.length; index += 1) {
      if (normalizeMediaId(media[index].getAttribute('data-media-id')) !== targetId) continue;
      var image =
        media[index].tagName === 'IMG'
          ? media[index]
          : media[index].querySelector && media[index].querySelector('img');
      var source = imageSource(image);
      if (source) return source;
    }

    return '';
  }

  // The base image comes from the template's media key only. Alt text and
  // data-title cannot tell a flat lay apart from a model shot, and a pet must
  // never land on model imagery, so an unconfigured color reports itself here
  // instead of guessing at a gallery slide.
  function listGalleryMedia(doc) {
    if (!doc || typeof doc.querySelectorAll !== 'function') return [];

    var nodes = doc.querySelectorAll('[data-media-id]');
    var seen = {};
    var media = [];

    for (var index = 0; index < nodes.length; index += 1) {
      var node = nodes[index];
      var mediaId = normalizeMediaId(node.getAttribute('data-media-id'));
      if (!mediaId || seen[mediaId]) continue;

      var image = node.tagName === 'IMG' ? node : node.querySelector && node.querySelector('img');
      seen[mediaId] = true;
      media.push({
        mediaId: mediaId,
        title:
          node.getAttribute('data-title') ||
          (image && typeof image.getAttribute === 'function' ? image.getAttribute('alt') : '') ||
          '',
      });
    }

    return media;
  }

  function selectedBackground(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return '';
    var selected =
      doc.querySelector('input[name*="-Color-"]:checked') ||
      doc.querySelector('input[name*="-Background-"]:checked') ||
      doc.querySelector('input[name*="-Colour-"]:checked') ||
      doc.querySelector('input[name*="Color"]:checked');
    if (selected && selected.value) return String(selected.value).trim();

    var background = doc.querySelector(
      '.backgroundHiddenInput:not(.backgroundHiddenInputBandana)'
    );
    return background && background.value ? String(background.value).trim() : '';
  }

  function productColors(doc) {
    if (!doc || typeof doc.getElementById !== 'function') return [];

    var node = doc.getElementById('pjDesignLabPlacementRequiredColors');
    if (!node) return [];

    try {
      var parsed = JSON.parse(node.textContent || node.innerHTML || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  // Colors reach us three ways: chosen by the shopper, implied because the
  // product only sells one color, or not at all on a color-split product where
  // the lone template owns the page. Only a real multi-color product with
  // nothing chosen yet gets the ticket's Pink Dream default.
  function resolveColor(doc, contextColor) {
    var chosen = contextColor == null ? '' : String(contextColor).trim();
    if (chosen) return chosen;

    var colors = productColors(doc).filter(function (color) {
      return color != null && String(color).trim();
    });
    if (colors.length === 1) return String(colors[0]).trim();
    if (colors.length > 1) return 'Pink Dream';

    return '';
  }

  function buildComposition(placement, context) {
    var petImages = context && Array.isArray(context.petImages) ? context.petImages : [];
    if (!placement || !placement.ok) {
      return { ok: false, reason: (placement && placement.reason) || 'missing-template' };
    }
    if (!placement.imageUrl && !placement.mediaId) {
      return { ok: false, reason: 'missing-flat-lay' };
    }
    if (petImages.length < placement.petCount || petImages.slice(0, placement.petCount).some(function (url) {
      return !url;
    })) {
      return { ok: false, reason: 'missing-pet-image' };
    }

    var settings = context && Array.isArray(context.petSettings) ? context.petSettings : [];
    return {
      ok: true,
      color: placement.color,
      colorKey: placement.colorKey,
      mediaId: placement.mediaId,
      imageUrl: placement.imageUrl,
      pets: placement.slots.map(function (slot, index) {
        var petSetting = settings[index] || {};
        return {
          imageUrl: petSetting.processedImage || petImages[index],
          slot: slot,
          active: index === Math.max(0, Number(context && context.activePet) || 0),
          processed: Boolean(petSetting.processedImage),
          // A failed pet shows its untouched upload rather than the previous
          // mode's output, so no stale cutout is passed off as the new mode.
          processingFailed: Boolean(petSetting.processingError),
          extractionMode: petSetting.extractionMode || 'background',
          style: petSetting.style || '',
          accessory: petSetting.accessory || '',
          accessoryOverlay: petSetting.accessoryOverlay || null,
          scale: Number(petSetting.scale) > 0 ? Number(petSetting.scale) : 1,
          offsetX: Number(petSetting.offsetX) || 0,
          offsetY: Number(petSetting.offsetY) || 0,
          eraseMaskUrl: petSetting.eraseMaskUrl || '',
        };
      }),
    };
  }

  function createPreviewRenderer(doc, options) {
    var config = options || {};
    var context = {};
    var zoomScale = ZOOM_DEFAULT;
    var initialized = false;

    function region(name) {
      var modal = doc.getElementById('pvModal');
      return modal && modal.querySelector('[data-pv-region="' + name + '"]');
    }

    function showError(reason) {
      var loaded = region('loaded');
      var error = region('error');
      // Every suppression reason is logged, not just the template ones: a blank
      // canvas with no console trail is undiagnosable from a QA report.
      if (root.console && typeof root.console.warn === 'function') {
        root.console.warn('[PJ Design Lab] Preview unavailable.', {
          reason: reason,
          petCount: context.petCount,
          petImages: context.petImages,
          petSettings: context.petSettings,
        });
      }
      if (loaded) loaded.hidden = true;
      if (!error) return false;

      error.hidden = false;
      error.innerHTML = '';

      var panel = doc.createElement('div');
      panel.className = 'pv-render-error';

      var title = doc.createElement('strong');
      title.textContent = 'Preview unavailable';

      var message = doc.createElement('p');
      message.textContent =
        reason === 'missing-template' || reason === 'missing-slots'
          ? 'This pajama color is not configured for preview yet.'
          : 'We could not load the product preview. Your uploaded photos are still safe.';

      panel.appendChild(title);
      panel.appendChild(message);
      error.appendChild(panel);
      return false;
    }

    function compositeElement() {
      var loaded = region('loaded');
      if (!loaded) return null;
      if (typeof loaded.querySelector === 'function') {
        return loaded.querySelector('.pv-composite');
      }
      return Array.prototype.find.call(loaded.children || [], function (child) {
        return child.className === 'pv-composite';
      });
    }

    // Off unless the shop metafield turns it on (see pj-design-lab-preview-modal.liquid).
    function watermarkEnabled() {
      var modal = doc.getElementById('pvModal');
      return Boolean(modal && modal.dataset && modal.dataset.watermarkEnabled === 'true');
    }

    // Display only: laid over the composite as its own layer, never drawn into
    // the pet images, which are the same files that go to checkout and print.
    function createWatermark() {
      var mark = doc.createElement('div');
      mark.className = 'pv-composite-watermark';
      mark.setAttribute('aria-hidden', 'true');
      return mark;
    }

    // Right-click "Save image" and dragging an image out both reach the clean
    // pet file beneath the mark, so neither is offered while it is on.
    function onSaveAttempt(event) {
      if (!watermarkEnabled()) return;
      var target = event && event.target;
      if (!target || typeof target.closest !== 'function' || !target.closest('.pv-composite')) {
        return;
      }
      event.preventDefault();
    }

    function applyZoom() {
      var frame = compositeElement();
      if (frame) frame.style.transform = 'scale(' + zoomScale + ')';
      return zoomScale;
    }

    function setZoom(nextScale) {
      zoomScale = clampZoom(nextScale);
      return applyZoom();
    }

    function zoomIn() {
      return setZoom(zoomScale + ZOOM_STEP);
    }

    function zoomOut() {
      return setZoom(zoomScale - ZOOM_STEP);
    }

    function resetZoom() {
      return setZoom(ZOOM_DEFAULT);
    }

    function createPet(pet, index) {
      var image = doc.createElement('img');
      image.className = 'pv-composite-pet';
      if (pet.extractionMode === 'background') image.classList.add('mode-background');
      if (pet.processingFailed) image.classList.add('is-unprocessed');
      if (pet.active) image.classList.add('sel-ring');
      image.src = pet.imageUrl;
      image.alt = 'Uploaded pet ' + (index + 1);
      image.dataset.petIndex = String(index);
      // Reposition is clamped to the slot's own size so a shopper can't drag
      // a pet fully off the flat-lay it's meant to sit on.
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' + (Number(pet.slot.rotation) || 0) + 'deg)';

      // Erase/restore edits are a "keep" stencil (opaque = keep, transparent
      // = erased) applied over this pet's own photo via CSS mask - never
      // swapped in as the image itself - so each pet keeps its own picture
      // even when "apply this look to all pets" shares the same stencil.
      //
      // mask-mode is forced to "alpha" rather than left at its "match-source"
      // default: WebKit (Safari, and therefore every browser on iOS) has long
      // defaulted a raster mask-image to *luminance* mode, under which an
      // opaque-but-dark stencil pixel reads as "hide" instead of "keep". The
      // stencil itself is painted white for "keep" (see pj-design-lab-review.js)
      // as a second layer of defense, but the mode is pinned explicitly too so
      // this never again depends on a browser's unstated default.
      if (pet.eraseMaskUrl) {
        var maskValue = 'url("' + pet.eraseMaskUrl + '")';
        image.style.maskImage = maskValue;
        image.style.webkitMaskImage = maskValue;
        image.style.maskMode = 'alpha';
        image.style.webkitMaskMode = 'alpha';
        image.style.maskSize = '100% 100%';
        image.style.webkitMaskSize = '100% 100%';
        image.style.maskRepeat = 'no-repeat';
        image.style.webkitMaskRepeat = 'no-repeat';
      }
      return image;
    }

    // The accessory is a sibling of the pet's image rather than a wrapper
    // around it, so the review editor keeps finding .pv-composite-pet exactly
    // where it did. .pv-composite-pet is aspect-ratio: 1, so a pet's box is
    // square and the catalog's placement percentages - both measured against
    // that box, as the wireframe measures them - transfer directly.
    //
    // The vertical offset is expressed as a share of the accessory's own
    // height: a `top` percentage resolves against the composite's height,
    // while the pet's box is sized off the composite's width, so the two are
    // not interchangeable. Ordering the transform as centre -> rotate -> lift
    // also keeps the accessory square to a rotated slot.
    function createAccessory(pet, index, asset, isBack) {
      var overlay = pet.accessoryOverlay;
      // Both the artwork and its geometry come from the catalog, and the
      // controls layer only hands over an overlay once it has both - there is
      // no geometry to invent here, so an incomplete one simply draws nothing.
      var placement = overlay && overlay.placement;
      if (!overlay || !asset || !placement) return null;

      var width = Number(placement.width);
      var bottom = Number(placement.bottom);
      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(bottom)) return null;

      // Distance from the pet's centre up to the accessory's bottom edge,
      // restated as a percentage of the accessory's own height.
      var lift = 50 + ((bottom - 50) / width) * 100;

      var image = doc.createElement('img');
      image.className = isBack ? 'pv-acc-overlay pv-acc-overlay--back' : 'pv-acc-overlay';
      image.src = asset;
      image.alt = '';
      image.dataset.petIndex = String(index);
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale * (width / 100) + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' +
        (Number(pet.slot.rotation) || 0) +
        'deg) translateY(-' +
        lift +
        '%)';
      return image;
    }

    function render(nextContext) {
      if (nextContext) context = Object.assign({}, context, nextContext);

      var placementApi = config.placement || root.PjDesignLabPlacement;
      var loadedPlacement = placementApi && placementApi.current && placementApi.current();
      var color = (config.resolveColor || resolveColor)(doc, context.color);
      var placement =
        loadedPlacement &&
        placementApi.getSlots(loadedPlacement.catalog, {
          color: color,
          mediaId: context.mediaId,
          petCount: context.petCount,
        });
      if (
        !placement ||
        (!placement.ok &&
          ['missing-catalog', 'missing-template', 'missing-slots'].indexOf(placement.reason) >= 0)
      ) {
        if (root.console && typeof root.console.warn === 'function') {
          root.console.warn(
            '[PJ Design Lab] Preview suppressed: no usable placement template.',
            {
              reason: (placement && placement.reason) || 'missing-template',
              requestedColor: color || '(no color axis on this product)',
              requestedColorKey:
                placementApi && placementApi.normalizeColorKey
                  ? placementApi.normalizeColorKey(color)
                  : color,
              requestedPetCount: context.petCount,
              placement: placement,
              galleryMedia: (config.listGalleryMedia || listGalleryMedia)(doc),
              diagnostics:
                placementApi && placementApi.diagnose ? placementApi.diagnose(doc) : null,
              hint: 'Compare requestedColorKey against templates[].colorKey, and requestedPetCount against templates[].petCountsConfigured.',
            }
          );
        }
      }
      var composition = buildComposition(placement, context);
      if (!composition.ok) return showError(composition.reason);

      var flatLay =
        composition.imageUrl ||
        (config.resolveProductImage || resolveProductImage)(doc, composition.mediaId);
      if (!flatLay) return showError('missing-flat-lay');

      var loaded = region('loaded');
      var error = region('error');
      if (!loaded) return false;

      loaded.innerHTML = '';
      loaded.hidden = false;
      if (error) error.hidden = true;

      var frame = doc.createElement('div');
      frame.className = 'pv-composite';
      frame.dataset.color = composition.colorKey;
      frame.dataset.mediaId = composition.mediaId;
      frame.style.transform = 'scale(' + zoomScale + ')';

      var base = doc.createElement('img');
      base.className = 'pv-composite-base';
      base.src = flatLay;
      base.alt = composition.color + ' pajamas preview';
      frame.appendChild(base);

      var overlay = doc.createElement('div');
      overlay.className = 'pv-composite-overlay';
      composition.pets.forEach(function (pet, index) {
        // An accessory that wraps behind the pet is drawn as a second layer
        // under it, so the pet hides whatever of that layer it overlaps and
        // the artwork reads as worn rather than stamped on top. Order is
        // back layer -> pet -> front layer.
        var accessory = pet.accessoryOverlay;
        var back = accessory && createAccessory(pet, index, accessory.backAsset, true);
        if (back) overlay.appendChild(back);
        overlay.appendChild(createPet(pet, index));
        var front = accessory && createAccessory(pet, index, accessory.asset, false);
        if (front) overlay.appendChild(front);
      });
      frame.appendChild(overlay);
      if (watermarkEnabled()) {
        frame.dataset.watermarked = 'true';
        frame.appendChild(createWatermark());
      }
      loaded.appendChild(frame);

      // The composite is torn down and rebuilt from scratch on every render,
      // so anything overlaid on a pet image (the review editor's drag/erase
      // surface) needs to know to re-attach itself to the new element.
      if (typeof doc.dispatchEvent === 'function' && typeof root.CustomEvent === 'function') {
        doc.dispatchEvent(
          new root.CustomEvent('pj-design-lab:composite-rendered', {
            detail: { activePet: Math.max(0, Number(context.activePet) || 0) },
          })
        );
      }
      return true;
    }

    // --- Flat-lay composite export (design-persistence) ---------------------
    // The live preview above is a stack of positioned/rotated <img> elements;
    // this bakes the same geometry into a single raster image for the cart
    // thumbnail, since Shopify line-item properties need a URL, not a DOM
    // tree. Every formula here is the pixel-space twin of createPet()'s and
    // createAccessory()'s CSS: same slot/offset/scale/rotation percentages,
    // same square (aspect-ratio: 1) pet/accessory boxes, same "cover" fit,
    // same erase-mask destination-in bake, same rotated-local-frame lift for
    // the accessory. Never throws - a failed export just means no thumbnail,
    // not a broken Add to Cart.

    // Fetches the bytes directly and loads the image from a same-origin blob
    // URL, rather than setting crossOrigin='anonymous' on an <img src="...">.
    // The pet/accessory images here are frequently already displayed
    // elsewhere on the page (the live Design Lab preview itself) via a plain
    // <img> with no crossOrigin attribute; browsers cache a resource by its
    // CORS mode along with its URL, so a later crossOrigin='anonymous'
    // request for the same URL can silently fail against that already-cached
    // opaque response. A blob URL sidesteps this entirely - no cross-origin
    // request happens at Image-decode time at all.
    function loadCompositeImage(src) {
      if (!src) return Promise.reject(new Error('no image url'));
      return root
        .fetch(src)
        .then(function (response) {
          if (!response.ok) throw new Error('image fetch failed: ' + src);
          return response.blob();
        })
        .then(function (blob) {
          return new Promise(function (resolve, reject) {
            var objectUrl = root.URL.createObjectURL(blob);
            var image = new root.Image();
            image.onload = function () {
              root.URL.revokeObjectURL(objectUrl);
              resolve(image);
            };
            image.onerror = function () {
              root.URL.revokeObjectURL(objectUrl);
              reject(new Error('image failed to decode: ' + src));
            };
            image.src = objectUrl;
          });
        });
    }

    function computePetGeometry(canvasWidth, canvasHeight, pet) {
      var widthPx = ((Number(pet.slot.width) || 0) * pet.scale * canvasWidth) / 100;
      return {
        widthPx: widthPx,
        // .pv-composite-pet is aspect-ratio: 1 - a square box sized by width,
        // not by the percentage-of-height that a literal height% would give.
        heightPx: widthPx,
        centerX: (clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) * canvasWidth) / 100,
        centerY: (clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) * canvasHeight) / 100,
        rotation: ((Number(pet.slot.rotation) || 0) * Math.PI) / 180,
      };
    }

    function drawCover(ctx, image, boxWidth, boxHeight) {
      var srcW = image.naturalWidth || image.width;
      var srcH = image.naturalHeight || image.height;
      if (!srcW || !srcH) return;
      var scale = Math.max(boxWidth / srcW, boxHeight / srcH);
      var drawW = srcW * scale;
      var drawH = srcH * scale;
      ctx.drawImage(image, -drawW / 2, -drawH / 2, drawW, drawH);
    }

    function drawPetLayer(ctx, geom, petImage, eraseMaskImage) {
      ctx.save();
      ctx.translate(geom.centerX, geom.centerY);
      ctx.rotate(geom.rotation);
      ctx.beginPath();
      ctx.rect(-geom.widthPx / 2, -geom.heightPx / 2, geom.widthPx, geom.heightPx);
      ctx.clip();
      if (eraseMaskImage) {
        // Same "keep" stencil the CSS mask uses (opaque = keep), baked via
        // destination-in instead of mask-image - see flattenErasedCutout in
        // pj-design-lab-controls.js for the same pattern on a single pet.
        var maskCanvas = doc.createElement('canvas');
        maskCanvas.width = geom.widthPx;
        maskCanvas.height = geom.heightPx;
        var maskCtx = maskCanvas.getContext('2d');
        var srcW = petImage.naturalWidth || petImage.width;
        var srcH = petImage.naturalHeight || petImage.height;
        var scale = Math.max(geom.widthPx / srcW, geom.heightPx / srcH);
        var drawW = srcW * scale;
        var drawH = srcH * scale;
        maskCtx.drawImage(
          petImage,
          (geom.widthPx - drawW) / 2,
          (geom.heightPx - drawH) / 2,
          drawW,
          drawH,
        );
        maskCtx.globalCompositeOperation = 'destination-in';
        maskCtx.drawImage(eraseMaskImage, 0, 0, geom.widthPx, geom.heightPx);
        ctx.drawImage(maskCanvas, -geom.widthPx / 2, -geom.heightPx / 2, geom.widthPx, geom.heightPx);
      } else {
        drawCover(ctx, petImage, geom.widthPx, geom.heightPx);
      }
      ctx.restore();
    }

    function drawAccessoryLayer(ctx, geom, accessoryImage, overlayPlacement) {
      var width = Number(overlayPlacement.width);
      var bottom = Number(overlayPlacement.bottom);
      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(bottom)) return;

      // Same lift formula as createAccessory(): distance from the pet's
      // centre to the accessory's bottom edge, as a % of the accessory's
      // own height.
      var lift = 50 + ((bottom - 50) / width) * 100;
      var accWidthPx = geom.widthPx * (width / 100);
      var accHeightPx = accWidthPx;

      ctx.save();
      ctx.translate(geom.centerX, geom.centerY);
      ctx.rotate(geom.rotation);
      // Lift is applied in the already-rotated local frame, same as the CSS
      // transform order (translate -50%/-50% then rotate then translateY) -
      // the accessory rides the pet's rotation rather than staying upright.
      ctx.translate(0, -(lift / 100) * accHeightPx);
      ctx.beginPath();
      ctx.rect(-accWidthPx / 2, -accHeightPx / 2, accWidthPx, accHeightPx);
      ctx.clip();
      drawCover(ctx, accessoryImage, accWidthPx, accHeightPx);
      ctx.restore();
    }

    function exportComposite() {
      if (
        typeof doc.createElement !== 'function' ||
        typeof root.Image === 'undefined' ||
        typeof root.Promise === 'undefined'
      ) {
        return Promise.resolve(null);
      }

      var placementApi = config.placement || root.PjDesignLabPlacement;
      var loadedPlacement = placementApi && placementApi.current && placementApi.current();
      var color = (config.resolveColor || resolveColor)(doc, context.color);
      var placement =
        loadedPlacement &&
        placementApi.getSlots(loadedPlacement.catalog, {
          color: color,
          mediaId: context.mediaId,
          petCount: context.petCount,
        });
      var composition = placement && buildComposition(placement, context);
      if (!composition || !composition.ok) return Promise.resolve(null);

      var flatLay =
        composition.imageUrl ||
        (config.resolveProductImage || resolveProductImage)(doc, composition.mediaId);
      if (!flatLay) return Promise.resolve(null);

      return loadCompositeImage(flatLay)
        .then(function (baseImage) {
          var canvas = doc.createElement('canvas');
          canvas.width = baseImage.naturalWidth || baseImage.width;
          canvas.height = baseImage.naturalHeight || baseImage.height;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

          var layerPromises = composition.pets.map(function (pet) {
            var accessory = pet.accessoryOverlay;
            return Promise.all([
              loadCompositeImage(pet.imageUrl).catch(function () {
                return null;
              }),
              pet.eraseMaskUrl
                ? loadCompositeImage(pet.eraseMaskUrl).catch(function () {
                    return null;
                  })
                : Promise.resolve(null),
              accessory && accessory.backAsset
                ? loadCompositeImage(accessory.backAsset).catch(function () {
                    return null;
                  })
                : Promise.resolve(null),
              accessory && accessory.asset
                ? loadCompositeImage(accessory.asset).catch(function () {
                    return null;
                  })
                : Promise.resolve(null),
            ]).then(function (results) {
              return {
                pet: pet,
                petImage: results[0],
                eraseMaskImage: results[1],
                backAccessoryImage: results[2],
                frontAccessoryImage: results[3],
              };
            });
          });

          return Promise.all(layerPromises).then(function (layers) {
            layers.forEach(function (layer) {
              // A pet whose own image failed to load draws nothing rather
              // than aborting the whole export - a partial thumbnail beats
              // none.
              if (!layer.petImage) return;
              var geom = computePetGeometry(canvas.width, canvas.height, layer.pet);
              var overlayPlacement =
                layer.pet.accessoryOverlay && layer.pet.accessoryOverlay.placement;
              if (layer.backAccessoryImage && overlayPlacement) {
                drawAccessoryLayer(ctx, geom, layer.backAccessoryImage, overlayPlacement);
              }
              drawPetLayer(ctx, geom, layer.petImage, layer.eraseMaskImage);
              if (layer.frontAccessoryImage && overlayPlacement) {
                drawAccessoryLayer(ctx, geom, layer.frontAccessoryImage, overlayPlacement);
              }
            });

            return new Promise(function (resolve) {
              canvas.toBlob(function (blob) {
                resolve(blob || null);
              }, 'image/png');
            });
          });
        })
        .then(function (blob) {
          if (!blob) return null;
          var modal = doc.getElementById('pvModal');
          var endpoint = modal && modal.dataset && modal.dataset.uploadUrl;
          var apiToken = modal && modal.dataset && modal.dataset.apiToken;
          if (!endpoint || !apiToken || typeof root.fetch !== 'function') return null;

          var formData = new root.FormData();
          formData.append('image', blob, 'flat-lay-composite.png');
          return root
            .fetch(endpoint, {
              method: 'POST',
              headers: { 'api-token': apiToken },
              body: formData,
            })
            .then(function (response) {
              return response.ok ? response.json() : null;
            })
            .then(function (result) {
              if (!result || !result.imageUrl) return null;
              return /^https?:\/\//i.test(result.imageUrl)
                ? result.imageUrl
                : new root.URL(result.imageUrl, endpoint).toString();
            });
        })
        .catch(function () {
          return null;
        });
    }
    // --- End flat-lay composite export --------------------------------------

    function setContext(nextContext) {
      context = Object.assign({}, context, nextContext || {});
      return context;
    }

    function onLoadingComplete() {
      render();
    }

    function onLoadingFailed() {
      render();
    }

    function onVariantChange() {
      if (!context || !context.petCount) return;
      context.color = selectedBackground(doc);
      render();
    }

    function onZoomClick(event) {
      var target = event && event.target;
      if (!target) return;
      if (target.id === 'pvZoomIn') zoomIn();
      if (target.id === 'pvZoomOut') zoomOut();
    }

    function previewIsOpen() {
      var modal = doc.getElementById('pvModal');
      return Boolean(modal && modal.classList && modal.classList.contains('open'));
    }

    function onZoomKeydown(event) {
      if (!previewIsOpen() || isEditableTarget(event && event.target)) return;
      var delta = zoomDeltaFromKey(event);
      if (!delta) return;
      if (typeof event.preventDefault === 'function') event.preventDefault();
      setZoom(zoomScale + delta);
    }

    function init() {
      if (initialized || !doc || typeof doc.addEventListener !== 'function') return;
      initialized = true;
      doc.addEventListener('pj-design-lab:preview-loading-complete', onLoadingComplete);
      doc.addEventListener('pj-design-lab:preview-loading-failed', onLoadingFailed);
      doc.addEventListener('variant:change', onVariantChange);
      doc.addEventListener('change', onVariantChange);
      doc.addEventListener('click', onZoomClick);
      doc.addEventListener('keydown', onZoomKeydown);
      doc.addEventListener('contextmenu', onSaveAttempt);
      doc.addEventListener('dragstart', onSaveAttempt);
    }

    init();

    return {
      setContext: setContext,
      render: render,
      setZoom: setZoom,
      zoomIn: zoomIn,
      zoomOut: zoomOut,
      resetZoom: resetZoom,
      exportComposite: exportComposite,
      init: init,
    };
  }

  var controller = root.document ? createPreviewRenderer(root.document) : null;

  return {
    normalizeMediaId: normalizeMediaId,
    resolveProductImage: resolveProductImage,
    listGalleryMedia: listGalleryMedia,
    selectedBackground: selectedBackground,
    productColors: productColors,
    resolveColor: resolveColor,
    buildComposition: buildComposition,
    clampPercent: clampPercent,
    clampZoom: clampZoom,
    zoomDeltaFromKey: zoomDeltaFromKey,
    createPreviewRenderer: createPreviewRenderer,
    setContext: controller && controller.setContext,
    render: controller && controller.render,
    setZoom: controller && controller.setZoom,
    zoomIn: controller && controller.zoomIn,
    zoomOut: controller && controller.zoomOut,
    resetZoom: controller && controller.resetZoom,
    exportComposite: controller && controller.exportComposite,
    init: controller && controller.init,
  };
});
